import { Component, OnInit, Inject, ViewChild, ChangeDetectorRef } from '@angular/core';
import { MatStepper } from '@angular/material/stepper';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { REQUISITION_ENTITY_SERVICE_TOKEN } from '../../../../injection-tokens/entity-service-tokens';
import { IRequisitionEntityService } from '../../../../interfaces/entities/requisition-entity-service-interface';
import { ResourceCreatorService } from '../../../../services/resource-creator.service';
import { ResourceMasterDataService } from '../../../../services/resource-master-data.service';
import { SoftwareFilterService } from '../../../../services/software-filter.service';
import { ResourceType } from '../../../../models/resource-meta-data';
import { Pair } from '../../../../models/pair';
import { Log, IShortLogger } from '../../../../services/logger.service';
import { BusinessProcessEntityService } from '../../../../services/settings/businessprocess-entity.service';
import { LoginEntityService } from '../../../../services/entity-services/login-entity.service';
// NOTE: slotTitle / slotDateRanges / slotTrainers removed — use reviewDateRanges + batchTrainer instead

/** Requires at least one of vmTemplateId or uaTemplateId to be set. */
function atLeastOneTemplate(ctrl: AbstractControl): ValidationErrors | null {
  const { vmTemplateId, uaTemplateId } = ctrl.value;
  return vmTemplateId || uaTemplateId ? null : { noTemplate: true };
}

interface TemplateOption {
  id: string;
  name: string;
  description: string;
  status?: string;
  os?: string;
  osVersion?: string;
  region?: string;
  size?: string;
  cloudProvider?: string;
  vCpu?: string;
  ramGb?: string;
  storageGb?: string;
  sdks?: string[];
  databases?: string[];
  editors?: string[];
  misc?: string[];
}

@Component({
  selector: 'app-trainer-requisition-view',
  templateUrl: './trainer-requisition-view.component.html',
  styleUrls: ['./trainer-requisition-view.component.css']
})
export class TrainerRequisitionViewComponent implements OnInit {
  private readonly logger: IShortLogger<TrainerRequisitionViewComponent> =
    Log.create(TrainerRequisitionViewComponent);

  readonly FORWARD_ACTIVITY_CODE = 'Cloud Settings';

  @ViewChild('stepper') stepper!: MatStepper;

  // ── Stepper FormGroups ────────────────────────────────────────────
  detailsForm    = new FormGroup({});
  scheduleForm   = new FormGroup({});
  additionalForm = new FormGroup({});
  templateForm   = new FormGroup(
    {
      vmTemplateId: new FormControl<string | null>(null),
      uaTemplateId: new FormControl<string | null>(null),
    },
    { validators: atLeastOneTemplate },
  );
  reviewForm = new FormGroup({});
  forwardForm = new FormGroup({
    team:     new FormControl<string | null>(null, Validators.required),
    user:     new FormControl<string | null>(null, Validators.required),
    remarks:  new FormControl<string>('', Validators.required),
  });

  // ── Forward form data ─────────────────────────────────────────────
  activities:   any[] = [];
  forwardTeams: any[] = [];
  forwardUsers: any[] = [];

  // ── Requisition data ──────────────────────────────────────────────
  requisitionData: any = null;
  trainingSchedules: any[] = [];
  reviewDateRanges: any[] = [];
  isPageLoading = true;
  private currentRequisitionId: string | null = null;

  /** Shortcut to the nested RequisitionData sub-object. */
  private get rd(): any {
    return this.requisitionData?.RequisitionData ?? this.requisitionData;
  }

  // ── VM Template autocomplete ──────────────────────────────────────
  vmTemplateQuery = '';
  vmTemplateResults: TemplateOption[] = [];
  selectedVmTemplate: TemplateOption | null = null;
  private allVmTemplates: TemplateOption[] = [];

  // ── UA Template autocomplete ──────────────────────────────────────
  uaTemplateQuery = '';
  uaTemplateResults: TemplateOption[] = [];
  selectedUaTemplate: TemplateOption | null = null;
  private allUaTemplates: TemplateOption[] = [];

  isForwarding = false;

  constructor(
    private route: ActivatedRoute,
    readonly router: Router,
    private snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef,
    @Inject(REQUISITION_ENTITY_SERVICE_TOKEN) private requisitionService: IRequisitionEntityService,
    private resourceCreatorService: ResourceCreatorService,
    private masterData: ResourceMasterDataService,
    private softwareFilterService: SoftwareFilterService,
    private businessProcessService: BusinessProcessEntityService,
    private loginEntityService: LoginEntityService,
  ) {}

  ngOnInit(): void {
    this.route.paramMap.subscribe(params => {
      const id = params.get('requisitionId');
      this.currentRequisitionId = id;
      if (id) {
        this.loadRequisition(id);
      } else {
        this.isPageLoading = false;
      }
    });
    this.preloadTemplates();
    this.loadActivities();

    this.forwardForm.get('team')?.valueChanges.subscribe(team => {
      this.forwardForm.get('user')?.reset();
      this.forwardUsers = [];
      if (team) this.loadUsersForTeam(team);
    });
  }

  // ── Data loading ──────────────────────────────────────────────────

  private loadRequisition(id: string): void {
    this.requisitionService.getRequisitionById(id).subscribe({
      next: (res: any) => {
        this.requisitionData = res?.data ?? res;
        this.trainingSchedules = this.requisitionData?.TrainingSchedulesData
          ?? this.requisitionData?.trainingSchedules ?? [];
        const slot = this.trainingSchedules[0];
        this.reviewDateRanges = slot
          ? (slot.DateRanges ?? slot.dateRanges ?? []).map((dr: any) => ({
              slotTitle: dr.SessionTitle ?? dr.Title ?? dr.title ?? '',
              startDate: dr.StartDate ?? dr.startDate,
              endDate:   dr.EndDate   ?? dr.endDate,
              startTime: dr.StartTime ?? dr.startTime ?? '',
              endTime:   dr.EndTime   ?? dr.endTime   ?? '',
            }))
          : [];
        this.logger.i('Requisition loaded:', this.requisitionData);
        this.restoreSelectedTemplates();
        this.isPageLoading = false;
        this.cdr.detectChanges();
        setTimeout(() => { if (this.stepper) this.stepper.selectedIndex = 3; });
      },
      error: (err: any) => {
        this.isPageLoading = false;
        this.logger.e('Failed to load requisition', err);
        this.snackBar.open('Failed to load requisition.', 'Dismiss', { duration: 5000 });
      }
    });
  }

  private async preloadTemplates(): Promise<void> {
    const regions = this.masterData.get<Pair[]>('cloud.azure.regions') ?? [];
    const sizes   = this.masterData.get<Pair[]>('cloud.azure.sizes')   ?? [];

    const getLabel = (list: Pair[], id: string): string =>
      list.find(p => p.id === id)?.name ?? id;

    const providerLabel = (raw: string): string => {
      const labels: Record<string, string> = {
        azure: 'Microsoft Azure',
        aws:   'Amazon AWS',
        gcp:   'Google Cloud',
      };
      return labels[(raw ?? '').toLowerCase()] ?? raw ?? '';
    };

    const mapNames = (arr: any[]): string[] =>
      (arr ?? []).map((s: any) => s.Name ?? s.name ?? String(s)).filter(Boolean);

    const mapModel = (m: any, hardware: boolean): TemplateOption => {
      const sw  = m.data?.['SoftwareData'] ?? {};
      const cpd = m.data?.['CloudProviderData'] ?? {};

      const osId      = sw.os;
      const osVerRaw  = sw.osVersion;
      const osInfo    = osId
        ? this.masterData.get<{ name: string; versions: Pair[] }>(`software.os.${osId}`)
        : null;
      const osName    = osInfo?.name ?? osId ?? '';
      const osVersion = osInfo?.versions?.find((v: Pair) => v.id === osVerRaw)?.name ?? osVerRaw ?? '';

      // Initialize filtered software lists and name arrays
      let sdkNames: string[] = [];
      let dbNames: string[] = [];
      let editorNames: string[] = [];
      let miscNames: string[] = [];

      if (osId && osVerRaw) {
        // Get master data lists for filtering
        const sdksData = this.masterData.get<any[]>('software.sdks') ?? [];
        const dbsData = this.masterData.get<any[]>('software.databases') ?? [];
        const editorsData = this.masterData.get<any[]>('software.editors') ?? [];
        const miscData = this.masterData.get<any[]>('software.misc') ?? [];

        // Apply OS-aware filtering to software installations
        const filteredSdks = this.softwareFilterService.filterByOs(sdksData, osId, osVerRaw);
        const filteredDbs = this.softwareFilterService.filterByOs(dbsData, osId, osVerRaw);
        const filteredEditors = this.softwareFilterService.filterByOs(editorsData, osId, osVerRaw);
        const filteredMisc = this.softwareFilterService.filterByOs(miscData, osId, osVerRaw);

        // Map filtered selected software to names
        const filteredSdkIds = (sw.sdks ?? []).filter((id: string) =>
          filteredSdks.some(s => s.id === id)
        );
        const filteredDbIds = (sw.databases ?? []).filter((id: string) =>
          filteredDbs.some(d => d.id === id)
        );
        const filteredEditorIds = (sw.editors ?? []).filter((id: string) =>
          filteredEditors.some(e => e.id === id)
        );
        const filteredMiscIds = (sw.misc ?? []).filter((id: string) =>
          filteredMisc.some(m => m.id === id)
        );

        sdkNames = filteredSdkIds.map((id: string) => filteredSdks.find(s => s.id === id)?.name ?? id);
        dbNames = filteredDbIds.map((id: string) => filteredDbs.find(d => d.id === id)?.name ?? id);
        editorNames = filteredEditorIds.map((id: string) => filteredEditors.find(e => e.id === id)?.name ?? id);
        miscNames = filteredMiscIds.map((id: string) => filteredMisc.find(m => m.id === id)?.name ?? id);
      } else {
        // If OS or version not provided, include all selected software (no filtering)
        sdkNames = mapNames(sw.sdks);
        dbNames = mapNames(sw.databases);
        editorNames = mapNames(sw.editors);
        miscNames = mapNames(sw.misc);
      }

      return {
        id:          m.id,
        name:        m.data?.['TemplateDetailsData']?.templateName ?? m.metaData?.name ?? m.id,
        description: m.data?.['TemplateDetailsData']?.templateDescription ?? '',
        status:      m.status ?? '',
        ...(hardware ? {
          os:            osName,
          osVersion,
          region:        getLabel(regions, cpd.region ?? ''),
          size:          getLabel(sizes, cpd.size ?? ''),
          cloudProvider: providerLabel(cpd.providerName ?? cpd.provider ?? ''),
          vCpu:          String(cpd.vCpus ?? cpd.vCpu ?? ''),
          ramGb:         String(cpd.ramGb ?? cpd.ram ?? ''),
          storageGb:     String(cpd.storageGb ?? cpd.storage ?? ''),
          sdks:          sdkNames,
          databases:     dbNames,
          editors:       editorNames,
          misc:          miscNames,
        } : {}),
      };
    };

    this.resourceCreatorService.getResourcesByType('vm' as ResourceType)
      .then(models => { this.allVmTemplates = models.map(m => mapModel(m, true)); })
      .catch(err => this.logger.e('VM template load failed', err));

    this.resourceCreatorService.getResourcesByType('acc' as ResourceType)
      .then(models => { this.allUaTemplates = models.map(m => mapModel(m, false)); })
      .catch(err => this.logger.e('UA template load failed', err));
  }

  /**
   * Pre-populate the template autocomplete fields from ResourceTemplateIds that were
   * persisted on the previous forward. Called after the requisition data is loaded.
   * Waits for allVmTemplates / allUaTemplates to be ready (preloadTemplates is async)
   * so we retry until the lists are available, or give up after ~3 s.
   */
  private restoreSelectedTemplates(): void {
    const slot = this.trainingSchedules[0];
    const savedIds: { Id: string; Name: string; ResourceType: string }[] =
      slot?.ResourceTemplateIds ?? slot?.resourceTemplateIds ?? [];

    if (!savedIds.length) return;

    const vmEntry = savedIds.find(e => (e.ResourceType ?? '').toLowerCase() === 'vm');
    const uaEntry = savedIds.find(e => (e.ResourceType ?? '').toLowerCase() === 'acc');

    // Retry loop — preloadTemplates is async and may not have resolved yet
    let attempts = 0;
    const tryRestore = () => {
      const vmReady = !vmEntry || this.allVmTemplates.length > 0;
      const uaReady = !uaEntry || this.allUaTemplates.length > 0;

      if (!vmReady || !uaReady) {
        if (++attempts < 30) {
          setTimeout(tryRestore, 100);
        }
        return;
      }

      if (vmEntry) {
        const match = this.allVmTemplates.find(t => t.id === vmEntry.Id)
                   ?? { id: vmEntry.Id, name: vmEntry.Name, description: '', status: '' };
        this.selectedVmTemplate = match;
        this.vmTemplateQuery = match.name;
        this.templateForm.controls['vmTemplateId'].setValue(match.id);
      }

      if (uaEntry) {
        const match = this.allUaTemplates.find(t => t.id === uaEntry.Id)
                   ?? { id: uaEntry.Id, name: uaEntry.Name, description: '', status: '' };
        this.selectedUaTemplate = match;
        this.uaTemplateQuery = match.name;
        this.templateForm.controls['uaTemplateId'].setValue(match.id);
      }

      this.logger.i('Restored selected templates from saved ResourceTemplateIds:', { vmEntry, uaEntry });
      this.cdr.detectChanges();
    };

    tryRestore();
  }

  // ── VM autocomplete ───────────────────────────────────────────────

  onVmTemplateSearch(query: string): void {
    if (typeof query !== 'string') return;
    const q = query.toLowerCase();
    this.vmTemplateResults = q
      ? this.allVmTemplates.filter(t => t.name.toLowerCase().includes(q))
      : this.allVmTemplates.slice(0, 10);
  }

  onVmTemplateSelected(event: any): void {
    const t: TemplateOption = event.option.value;
    this.selectedVmTemplate = t;
    this.vmTemplateQuery = t.name;
    this.templateForm.controls['vmTemplateId'].setValue(t.id);
  }

  displayVmFn = (t: TemplateOption | string): string => {
    if (!t) return '';
    if (typeof t === 'string') return t;
    return t.name ?? '';
  };

  clearVmTemplate(): void {
    this.selectedVmTemplate = null;
    this.vmTemplateQuery = '';
    this.vmTemplateResults = [];
    this.templateForm.controls['vmTemplateId'].setValue(null);
  }

  // ── UA autocomplete ───────────────────────────────────────────────

  onUaTemplateSearch(query: string): void {
    if (typeof query !== 'string') return;
    const q = query.toLowerCase();
    this.uaTemplateResults = q
      ? this.allUaTemplates.filter(t => t.name.toLowerCase().includes(q))
      : this.allUaTemplates.slice(0, 10);
  }

  onUaTemplateSelected(event: any): void {
    const t: TemplateOption = event.option.value;
    this.selectedUaTemplate = t;
    this.uaTemplateQuery = t.name;
    this.templateForm.controls['uaTemplateId'].setValue(t.id);
  }

  displayUaFn = (t: TemplateOption | string): string => {
    if (!t) return '';
    if (typeof t === 'string') return t;
    return t.name ?? '';
  };

  clearUaTemplate(): void {
    this.selectedUaTemplate = null;
    this.uaTemplateQuery = '';
    this.uaTemplateResults = [];
    this.templateForm.controls['uaTemplateId'].setValue(null);
  }

  // ── Forward action ────────────────────────────────────────────────

  onForward(): void {
    if (this.forwardForm.invalid || this.isForwarding) {
      this.forwardForm.markAllAsTouched();
      return;
    }
    this.isForwarding = true;

    const { team, user, remarks } = this.forwardForm.value;
    const toTeam = this.forwardTeams.find((t: any) => t.team === team);
    const toUser = this.forwardUsers.find((u: any) => u.name === user);

    const movement = {
      requisitionId:       this.currentRequisitionId,
      purpose:             'Cloud Settings',
      purposeCode:         'CloudSettings',
      forwardToTeam:       team,
      forwardToTeamId:     toTeam?.teamId ?? '',
      forwardToUser:       user,
      forwardToUserId:     toUser?.id ?? '',
      forwardFromTeam:     localStorage.getItem('team')     ?? '',
      forwardFromTeamId:   localStorage.getItem('teamId')   ?? '',
      forwardFromUser:     localStorage.getItem('userName') ?? '',
      forwardFromUserId:   localStorage.getItem('userId')   ?? '',
      remarks,
      forwardedOn:         new Date(),
      isActive:            true,
      isRead:              false,
      isPurposeDone:       false,
      readDate:            null,
      srNo:                0,
      resourceTemplateIds: this.buildResourceTemplateIds(),
    };

    this.logger.i('Forwarding requisition:', movement);
    this.requisitionService.forwardRequisition(movement).subscribe({
      next: () => {
        this.isForwarding = false;
        this.snackBar.open('Requisition forwarded successfully.', 'OK', { duration: 5000 });
        this.router.navigate(['/requisitions']);
      },
      error: (err: any) => {
        this.isForwarding = false;
        const msg = err?.error?.message ?? err?.message ?? 'Failed to forward requisition.';
        this.snackBar.open(msg, 'Dismiss', { duration: 6000 });
        this.logger.e('Forward failed', err);
      },
    });
  }

  private buildResourceTemplateIds(): { Id: string; Name: string; ResourceType: string }[] {
    const ids: { Id: string; Name: string; ResourceType: string }[] = [];
    if (this.selectedVmTemplate) {
      ids.push({ Id: this.selectedVmTemplate.id, Name: this.selectedVmTemplate.name, ResourceType: 'vm' });
    }
    if (this.selectedUaTemplate) {
      ids.push({ Id: this.selectedUaTemplate.id, Name: this.selectedUaTemplate.name, ResourceType: 'acc' });
    }
    return ids;
  }

  private loadActivities(): void {
    this.businessProcessService.getAllBusinessProcess().subscribe({
      next: (result: any[]) => {
        const bp   = result.find((b: any) => b.name === 'Requisitions');
        const acts = bp?.activities ?? [];
        this.activities = acts.filter((a: any) =>
          a.code?.toLowerCase().trim() !== 'drafting'
        );
        // Cloud Settings is the fixed forwarding activity — pre-load its teams
        this.loadTeamsForActivity('CloudSettings');
      },
      error: (err: any) => this.logger.e('Failed to load activities', err),
    });
  }

  loadTeamsForActivity(activityCode: string): void {
    const act = this.activities.find((a: any) => a.code === activityCode);
    this.forwardTeams = act?.actions?.[0]?.rights?.teams ?? [];
  }

  loadUsersForTeam(team: string): void {
    this.loginEntityService.usersGetByTeam(team).subscribe({
      next: (users: any) => { this.forwardUsers = users ?? []; },
      error: (err: any) => this.logger.e('Failed to load users for team', err),
    });
  }

  get forwardActivityName(): string {
    return 'Cloud Settings';
  }

  // ── Read-only data helpers ────────────────────────────────────────

  get hasAtLeastOneTemplate(): boolean {
    return !!(this.templateForm.value.vmTemplateId || this.templateForm.value.uaTemplateId);
  }

  get customerDetails(): any {
    return this.rd?.customerDetails ?? this.rd?.CustomerDetails;
  }

  get enquiryId(): string {
    return this.rd?.enquiryId ?? this.rd?.EnquiryId ?? '';
  }

  get docNo(): string {
    return this.rd?.docNo ?? this.rd?.DocNo ?? '';
  }

  get docDate(): string {
    const d = this.rd?.docDate ?? this.rd?.DocDate;
    return d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '';
  }

  get customerType(): string {
    return this.customerDetails?.customerType ?? this.customerDetails?.CustomerType
        ?? this.customerDetails?.type ?? this.customerDetails?.Type ?? '';
  }

  get customerName(): string {
    return this.customerDetails?.name ?? this.customerDetails?.Name
        ?? this.customerDetails?.companyName ?? this.customerDetails?.CompanyName ?? '';
  }

  get batchTitle(): string {
    return this.trainingSchedules[0]?.Title ?? this.trainingSchedules[0]?.title ?? '';
  }

  get batchTrainer(): string {
    const trainers = this.trainingSchedules[0]?.Trainers ?? this.trainingSchedules[0]?.trainers ?? [];
    return trainers.map((t: any) => t.Name ?? t.name ?? '').filter(Boolean).join(', ');
  }

  get specialRequirements(): string {
    const ai = this.requisitionData?.AdditionalInformationData ?? this.requisitionData?.additionalInformationData;
    if (ai) return ai?.SpecialRequirements ?? ai?.specialRequirements ?? '';
    return this.requisitionData?.SpecialRequirements ?? this.requisitionData?.specialRequirements ?? '';
  }

  get finalComments(): string {
    const ai = this.requisitionData?.AdditionalInformationData ?? this.requisitionData?.additionalInformationData;
    if (ai) return ai?.FinalComments ?? ai?.finalComments ?? '';
    return this.requisitionData?.FinalComments ?? this.requisitionData?.finalComments ?? '';
  }

  get scheduleCount(): number {
    return this.trainingSchedules.length;
  }

  formatTime12h(time: string): string {
    if (!time) return '';
    const [h, m] = time.split(':').map(Number);
    const suffix = h < 12 ? 'AM' : 'PM';
    const hour   = h === 0 ? 12 : h > 12 ? h - 12 : h;
    return `${hour}:${String(m).padStart(2, '0')} ${suffix}`;
  }

  getScheduleDateRange(): string {
    const timestamps = this.trainingSchedules
      .flatMap((s: any) => s.DateRanges ?? s.dateRanges ?? [])
      .map((dr: any) => new Date(dr.StartDate ?? dr.startDate).getTime())
      .filter((t: number) => !isNaN(t));
    if (!timestamps.length) return '';
    const opts: Intl.DateTimeFormatOptions = { day: '2-digit', month: 'short', year: 'numeric' };
    const min = new Date(Math.min(...timestamps)).toLocaleDateString('en-GB', opts);
    const max = new Date(Math.max(...timestamps)).toLocaleDateString('en-GB', opts);
    return min === max ? min : `${min} \u2013 ${max}`;
  }
}
